import { Button, Link, Tag, Typography } from '@iheartjane/reefer';
import { sentenceCase } from '@iheartjane/utils';
import { clsx } from 'clsx';
import { useNavigation } from 'react-router';

import { GetDirectionsLink } from '@jane/bloom/components/getDirectionsLink';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useCustomLanguage } from '@jane/bloom/hooks/useCustomLanguage';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { stateNameToAbbreviation } from '@jane/bloom/utils/geo/stateNameToAbbreviation';
import { formatPhoneNumber } from '@jane/bloom/utils/strings/formatPhoneNumber';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';

import styles from './storeCard.module.css';

interface StoreCardProps {
  selected?: boolean;
  selectStore: (storeId: number) => void;
  store: StoreViewModel;
}

export function StoreCard({ selected, selectStore, store }: StoreCardProps) {
  const { state: navigationState } = useNavigation();

  const { inDeliveryRange, isUndeliverableAddress, searchReservationMode } =
    useStoreSwitcher();

  const { pinnedStore, shoppingPreferences } = useRootLoaderData();
  const customLanguage = useCustomLanguage();
  const isCurrentStore = pinnedStore.id === store.id;

  const preferencesHaveChanged =
    pinnedStore.id !== store.id ||
    shoppingPreferences?.reservationMode !== searchReservationMode;

  const desiredReservationMode =
    shoppingPreferences?.reservationMode === 'delivery'
      ? customLanguage.reservationModeLabels.pickup?.toLowerCase() || 'pickup'
      : customLanguage.reservationModeLabels.delivery?.toLowerCase() ||
        'delivery';

  const isLoading = navigationState !== 'idle' && selected;

  return (
    <div
      className={clsx(styles['storeCard'], {
        [styles['storeCard--selected']]: isCurrentStore,
      })}
      data-testid="store-switcher-store-card"
    >
      <div className={styles['storeCardInner']}>
        <div className={styles['content']}>
          {isCurrentStore && (
            <div className={styles['header']}>
              <Tag className={styles['label']} mb={8} label={'My\xa0store'} />
              {store.distance && (
                <Tag className={styles['distanceTag']} label={store.distance} />
              )}
            </div>
          )}
          <div className={styles['header']}>
            <Typography maxLines={1} variant="body-semibold" color="inherit">
              {store.name}
            </Typography>
            {!isCurrentStore && store.distance && (
              <Tag className={styles['distanceTag']} label={store.distance} />
            )}
          </div>
          <Typography color="inherit">
            {[store.address, store.city, stateNameToAbbreviation(store.state)]
              .filter(Boolean)
              .join(', ')}
          </Typography>
          <Typography color="inherit">{store.formattedHours}</Typography>
          <div className={styles['storeType']}>
            {store.recreational ? (
              <Typography color="inherit" maxLines={1}>
                {sentenceCase(
                  store.store_compliance_language_settings?.['recreational'] ||
                    ''
                ) || 'Recreational'}
              </Typography>
            ) : (
              <Typography color="inherit" maxLines={1}>
                Medical
              </Typography>
            )}
          </div>
          {isCurrentStore && (
            <div className={styles['storeComms']}>
              <GetDirectionsLink
                color="inherit"
                mr={24}
                store={store}
                typography="body-regular"
              />
              <Link
                typography="body-regular"
                color="inherit"
                href={`tel:${store.phone}`}
              >{`Call ${formatPhoneNumber(store.phone, false)}`}</Link>
            </div>
          )}

          {isCurrentStore &&
            searchReservationMode !== shoppingPreferences?.reservationMode && (
              <Button
                label={`Switch to ${desiredReservationMode}`}
                full
                mt={16}
                onClick={() => selectStore(store.id)}
                loading={isLoading}
                disabled={
                  !preferencesHaveChanged ||
                  isUndeliverableAddress ||
                  !inDeliveryRange
                }
              />
            )}

          {!isCurrentStore && (
            <Button
              label="Select store"
              full
              mt={8}
              onClick={() => selectStore(store.id)}
              loading={isLoading}
              disabled={
                !preferencesHaveChanged ||
                isUndeliverableAddress ||
                !inDeliveryRange
              }
              className={styles['selectStoreButton']}
            />
          )}
        </div>
      </div>
    </div>
  );
}
